import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, DollarSign, Search, Users, X, Check,
  Loader2, Wallet, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

export default function AdminProfits() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [profitModalOpen, setProfitModalOpen] = useState(false);
  const [lossModalOpen, setLossModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form state
  const [form, setForm] = useState({ amount: '', note: '' });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUsersForProfit();
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term)
    );
  });

  const handleAddProfit = (user) => {
    setSelectedUser(user);
    setForm({ amount: '', note: '' });
    setProfitModalOpen(true);
  };

  const handleAddLoss = (user) => {
    setSelectedUser(user);
    setForm({ amount: '', note: '' });
    setLossModalOpen(true);
  };

  const submitProfit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      setActionLoading(true);
      await adminAPI.addProfit(selectedUser.id, {
        user_id: selectedUser.id,
        amount: parseFloat(form.amount),
        is_profit: true,
        note: form.note
      });
      toast.success(`$${form.amount} profit added to ${selectedUser.name}'s account`);
      setProfitModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add profit');
    } finally {
      setActionLoading(false);
    }
  };

  const submitLoss = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      setActionLoading(true);
      await adminAPI.addLoss(selectedUser.id, {
        user_id: selectedUser.id,
        amount: parseFloat(form.amount),
        is_profit: false,
        note: form.note
      });
      toast.success(`$${form.amount} deducted from ${selectedUser.name}'s account`);
      setLossModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add loss');
    } finally {
      setActionLoading(false);
    }
  };

  // Calculate totals
  const totalBalance = users.reduce((sum, u) => sum + (u.balance || 0), 0);
  const totalProfit = users.reduce((sum, u) => sum + (u.total_profit || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Profit & Loss Management</h1>
          <p className="text-gray-400 text-sm mt-1">Add profits or losses to user accounts</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Users</p>
              <p className="text-2xl font-bold text-white">{users.length}</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-emerald-500/20 to-green-500/20 border border-emerald-500/30 rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total User Balance</p>
              <p className="text-2xl font-bold text-white">${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Profits Distributed</p>
              <p className="text-2xl font-bold text-white">${totalProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
          data-testid="profit-search-input"
        />
      </div>

      {/* Users Grid */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
          <p className="text-gray-400 mt-2">Loading users...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No users found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredUsers.map((user, index) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-5 hover:border-gray-600 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white font-semibold text-lg">
                  {user.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{user.name || 'Unknown'}</p>
                  <p className="text-gray-400 text-sm truncate">{user.email}</p>
                </div>
              </div>
              
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Current Balance</span>
                  <span className="text-emerald-400 font-bold">
                    ${(user.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Total Profit</span>
                  <span className="text-purple-400 font-semibold">
                    ${(user.total_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleAddProfit(user)}
                  className="flex items-center justify-center gap-2 py-2.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-xl transition-all text-sm font-medium"
                  data-testid={`add-profit-${user.id}`}
                >
                  <ArrowUpRight className="w-4 h-4" />
                  Add Profit
                </button>
                <button
                  onClick={() => handleAddLoss(user)}
                  className="flex items-center justify-center gap-2 py-2.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 rounded-xl transition-all text-sm font-medium"
                  data-testid={`add-loss-${user.id}`}
                >
                  <ArrowDownRight className="w-4 h-4" />
                  Add Loss
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add Profit Modal */}
      <AnimatePresence>
        {profitModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setProfitModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Add Profit</h2>
                    <p className="text-gray-400 text-sm">{selectedUser?.name}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                  <p className="text-gray-400 text-sm mb-1">Current Balance</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    ${(selectedUser?.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Profit Amount ($)</label>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white text-xl font-semibold focus:outline-none focus:border-emerald-500"
                    data-testid="profit-amount-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Note / Reason (optional)</label>
                  <textarea
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="e.g., Daily trading profit"
                    rows={3}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white resize-none focus:outline-none focus:border-emerald-500"
                    data-testid="profit-note-input"
                  />
                </div>

                {form.amount && parseFloat(form.amount) > 0 && (
                  <div className="bg-gray-700/30 rounded-xl p-4">
                    <p className="text-gray-400 text-sm mb-1">New Balance After Profit</p>
                    <p className="text-2xl font-bold text-white">
                      ${((selectedUser?.balance || 0) + parseFloat(form.amount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t border-gray-700 flex gap-3">
                <button
                  onClick={() => setProfitModalOpen(false)}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitProfit}
                  disabled={actionLoading}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white rounded-xl transition-all flex items-center justify-center gap-2"
                  data-testid="confirm-profit-btn"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  Add Profit
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Loss Modal */}
      <AnimatePresence>
        {lossModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setLossModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Add Loss</h2>
                    <p className="text-gray-400 text-sm">{selectedUser?.name}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <p className="text-gray-400 text-sm mb-1">Current Balance</p>
                  <p className="text-2xl font-bold text-red-400">
                    ${(selectedUser?.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Loss Amount ($)</label>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white text-xl font-semibold focus:outline-none focus:border-red-500"
                    data-testid="loss-amount-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Note / Reason (optional)</label>
                  <textarea
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="e.g., Market correction, fee deduction"
                    rows={3}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white resize-none focus:outline-none focus:border-red-500"
                    data-testid="loss-note-input"
                  />
                </div>

                {form.amount && parseFloat(form.amount) > 0 && (
                  <div className="bg-gray-700/30 rounded-xl p-4">
                    <p className="text-gray-400 text-sm mb-1">New Balance After Loss</p>
                    <p className="text-2xl font-bold text-white">
                      ${Math.max(0, (selectedUser?.balance || 0) - parseFloat(form.amount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t border-gray-700 flex gap-3">
                <button
                  onClick={() => setLossModalOpen(false)}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitLoss}
                  disabled={actionLoading}
                  className="flex-1 py-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white rounded-xl transition-all flex items-center justify-center gap-2"
                  data-testid="confirm-loss-btn"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  Add Loss
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
